import { authApi } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";

export interface AttachmentResponse {
	url: string;
	success: boolean;
	message?: string;
}

/**
 * Fetches attachment URL by key
 * @param key - The attachment key from backend
 * @returns Promise<AttachmentResponse> - Object containing the URL and success status
 */
export const getAttachmentUrl = async (key: string) => {
	try {
		// Encode the key to handle special characters and spaces
		const encodedKey = encodeURIComponent(key);
		// Axios interceptor returns response.data directly, so response is already unwrapped
		// Type assertion needed because interceptor unwraps the response
		const response = (await authApi.get(SERVER_ROUTES.INSTITUTE.GET_ATTACHMENT_URL(encodedKey))) as {
			success: boolean;
			data: string;
			message?: string;
			url?: string;
		};
		// API returns { success: true, data: "url_string", ... }
		// Extract URL from data field and map to url field for consistency
		return {
			url: response?.data || response?.url || "",
			success: response?.success ?? true,
			message: response?.message,
		};
	} catch (error) {
		console.error("Error fetching attachment URL:", error);
		return {
			url: "",
			success: false,
			message: "Failed to fetch attachment URL",
		};
	}
};

/**
 * Utility function to get attachment URL as a string
 * @param key - The attachment key
 * @returns Promise<string> - The attachment URL string or empty string if failed
 */
export const getAttachmentUrlString = async (key: string): Promise<string> => {
	const result = await getAttachmentUrl(key);
	return result.url || "";
};

/**
 * Utility function to check if attachment key is valid
 * @param key - The attachment key to validate
 * @returns boolean - True if key is valid (not empty, null, or undefined)
 */
export const isValidAttachmentKey = (key: string | null | undefined): boolean => {
	return Boolean(key && key.trim() !== "");
};

/**
 * Converts a file path to use the new attachment API endpoint
 * @param filePath - The file path from the backend (e.g., "uploads/file.jpg")
 * @returns string - The attachment key to use with the API
 */
export const convertFilePathToAttachmentKey = (filePath: string): string => {
	// Remove any leading slashes and return the path as the key
	return filePath.replace(/^\/+/, "");
};

/**
 * Gets the attachment URL for a file path using the new API
 * @param filePath - The file path from the backend
 * @returns Promise<string> - The actual file URL or empty string if failed
 */
export const getAttachmentUrlFromFilePath = async (filePath: string) => {
	if (!filePath) return "";

	const attachmentKey = convertFilePathToAttachmentKey(filePath);
	return await getAttachmentUrlString(attachmentKey);
};



export const getFullFileUrl = (filePath: string) => {
	if (!filePath) return "";

	// If the filePath is already a full URL (including blob URLs), return it as is
	if (filePath.startsWith("http://") || filePath.startsWith("https://") || filePath.startsWith("blob:")) {
		return filePath;
	}

	// For regular file paths, return as is (they might need to be processed by the attachment API)
	return filePath;
}